<script setup lang="ts">
import {
  OperatorModuleViewNode,
  OperatorModuleViewTreeNode,
} from '@/views/organization/operator/OperatorModuleView.vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import TreeView from '@/components/tree/TreeView.vue'
import StepIconList from '@/components/icon/StepIconList.vue'
import StepChangeBanner from '@/components/operator/StepChangeBanner.vue'
import useTree, { TreeItemNode } from '@/composables/useTree'
import { useHorizResize, useVertResize } from '@/composables/useResizable'
import { StepChangeKind } from '@/generated/graphql'

const props = defineProps<{
  loading: boolean
  abbreviation: string
  nodes: OperatorModuleViewTreeNode[]
}>()

const { t } = useI18n()
const { treeMapItemIdsTo } = useTree()
const selectedStepId = defineModel<string | undefined>('selectedStepId', { default: undefined })
const activatedNodeId = ref<string>()

const nodeIdsToStepIds = computed(() =>
  treeMapItemIdsTo(props.nodes, (n) => (n.item as OperatorModuleViewNode).step.id),
)
const removedNodeIdsToStepIds = computed(() => {
  const map: Record<string, string> = {}
  const visit = (nodes: OperatorModuleViewTreeNode[]) =>
    nodes.forEach((n) => {
      if (n.item.removed) map[n.item.id] = n.item.step.id
      visit(n.children)
    })
  visit(props.nodes)
  return map
})
watch(activatedNodeId, (v, previous) => {
  // A ghost of a removed step opens its old version instead of becoming the selected step
  if (v && removedNodeIdsToStepIds.value[v]) {
    openRemovedStep(removedNodeIdsToStepIds.value[v])
    nextTick(() => (activatedNodeId.value = previous))
    return
  }
  selectedStepId.value = v ? nodeIdsToStepIds.value[v] : undefined
})
watch(
  [selectedStepId, nodeIdsToStepIds],
  () => {
    activatedNodeId.value = Object.keys(nodeIdsToStepIds.value).find(
      (k) => nodeIdsToStepIds.value[k] == selectedStepId.value,
    )
  },
  { immediate: true },
)

type NumberedNode = TreeItemNode<OperatorModuleViewNode> & { numbering: number[] }
const nodeProcess = (
  node: TreeItemNode<OperatorModuleViewNode>,
  prevNumbering: number[],
  currentNumber: number,
): NumberedNode => {
  const currentNumbering = node.item.removed ? [] : [...prevNumbering, currentNumber + 1]
  let childNumber = 0
  const children = node.children.map((child) =>
    nodeProcess(child, currentNumbering, child.item.removed ? 0 : childNumber++),
  )
  return {
    ...node,
    children,
    numbering: currentNumbering,
  }
}
const numberedNodes = computed(() => {
  let rootNumber = 0
  return props.nodes.map((n) => nodeProcess(n, [], n.item.removed ? 0 : rootNumber++))
})
const itemProps = (node: TreeItemNode<OperatorModuleViewNode>) => ({
  disabled: node.children.some((c) => !c.item.removed),
})

const {
  pct: leftPct,
  containerEl: viewportContainer,
  startDrag: startWidthDrag,
} = useHorizResize(65, 10, 90)
const drawerWidth = computed(() => Math.round((1 - leftPct.value / 100) * window.innerWidth))

onMounted(() => {
  viewportContainer.value = document.documentElement as HTMLElement
})

const drawerEl = ref<HTMLElement>()
const {
  pct: viewerPct,
  containerEl: drawerContainer,
  startDrag: startViewerDrag,
} = useVertResize(35, 15, 75)

const showRemovedDialog = ref(false)
const removedStepDialogId = ref<string>()
function openRemovedStep(stepId: string) {
  removedStepDialogId.value = stepId
  showRemovedDialog.value = true
}

watch(drawerEl, (el) => {
  if (el) drawerContainer.value = el
})
</script>

<template>
  <v-navigation-drawer location="right" :width="drawerWidth">
    <div class="drawer-row">
      <div class="resize-handle-v" @mousedown="startWidthDrag" />

      <div ref="drawerEl" class="drawer-body">
        <div class="overflow-y-auto" style="flex: 1; min-height: 0; margin-top: 1em">
          <tree-view
            v-model:activated="activatedNodeId"
            :nodes="numberedNodes"
            open-all
            :item-props="itemProps"
          >
            <template #title="{ item }">
              <span v-if="!item.item.removed" class="step-number"
                >{{ props.abbreviation }} {{ item.numbering.join('.') }}</span
              >
              <span :class="{ 'removed-step-name': item.item.removed }">
                {{ item.item.step.name }}
              </span>
              <step-icon-list :icons="item.item.step.icons" :size="18" class="ml-1" />
            </template>
            <template #subtitle="{ item }">
              <template v-if="item.children.every((c) => c.item.removed)">
                <span
                  v-if="item.item.changeKind == StepChangeKind.Modified"
                  class="changed-subtitle"
                >
                  <v-icon icon="warning" color="warning" start />
                  {{ t('component.operatorStepTree.changedDescription') }}
                </span>
                <span
                  v-else-if="item.item.changeKind == StepChangeKind.Added"
                  class="changed-subtitle"
                >
                  <v-icon icon="add_circle" color="success" start />
                  {{ t('component.operatorStepTree.addedDescription') }}
                </span>
                <span
                  v-else-if="item.item.changeKind == StepChangeKind.Moved"
                  class="changed-subtitle"
                >
                  <v-icon icon="swap_vert" color="purple" start />
                  {{
                    item.item.movedFrom?.length
                      ? t('component.operatorStepTree.movedFromDescription', {
                          position: `${props.abbreviation} ${item.item.movedFrom.join('.')}`,
                        })
                      : t('component.operatorStepTree.movedDescription')
                  }}
                </span>
                <span
                  v-else-if="item.item.changeKind == StepChangeKind.Removed"
                  class="changed-subtitle"
                >
                  <v-icon icon="remove_circle" color="error" start />
                  {{ t('component.operatorStepTree.removedDescription') }}
                </span>
              </template>
            </template>
            <template #append="{ item }">
              <v-chip density="compact">{{ item.item.step.stepType.name }}</v-chip>
            </template>
          </tree-view>
        </div>

        <div class="resize-handle-h" @mousedown="startViewerDrag" />

        <div :style="{ height: viewerPct + '%', minHeight: '150px' }">
          <slot name="bottom" />
        </div>
      </div>
    </div>
  </v-navigation-drawer>

  <v-dialog v-model="showRemovedDialog" max-width="1100">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t('component.stepChangeBanner.title') }}</span>
        <v-btn icon="close" variant="text" @click="showRemovedDialog = false" />
      </v-card-title>
      <v-card-text class="removed-step-dialog-body">
        <step-change-banner
          v-if="removedStepDialogId"
          :key="removedStepDialogId"
          :removed-step-id="removedStepDialogId"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.v-navigation-drawer {
  border: none !important;
}

:deep(.v-navigation-drawer__content) {
  padding: 0 !important;
  overflow: hidden !important;
}

.drawer-row {
  display: flex;
  height: 100%;
  min-height: 0;
}

.drawer-row > .resize-handle-v {
  margin: 0 5px;
}

.drawer-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.step-number {
  font-weight: bold;
  margin-right: 8px;
}

.changed-subtitle {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.68);

  .v-icon {
    font-size: 1rem;
  }
}

:deep(.v-list-item__subtitle) {
  opacity: 1;
}

.removed-step-name {
  text-decoration: line-through;
  opacity: 0.6;
}

.removed-step-dialog-body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
